import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import s from "./Library.module.css";
import { NewRevisionDialog } from "./NewRevisionDialog";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useAcknowledgeRevision, useDocument, useDocumentTypes, useTransitionRevision } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  attention,
  ATTENTION_LABEL,
  offeredTransitions,
  pendingAcknowledgement,
  REVISION_STATUS_LABEL,
  revisionByLabel,
  TRANSITION_VERB,
  transitionPermission,
  type DocumentType,
  type Revision,
  type RevisionStatus,
} from "../../domain/library";
import { Button, Card, ErrorState, Input, Skeleton } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { t } from "../../i18n";
import { ClassPill, StatusPill } from "./LibraryPage";

/**
 * One controlled document: what is in force, every revision that ever was
 * (superseded ones included — the history is the control), what it is
 * linked to, and who has acknowledged the current issue.
 */
export function DocumentPage() {
  const { id = "" } = useParams();
  const { site, serverTime, principal } = useSession();
  const doc = useDocument(id);
  const types = useDocumentTypes();
  const now = useMemo(() => new Date(serverTime), [serverTime]);
  const [drafting, setDrafting] = useState(false);
  const acknowledge = useAcknowledgeRevision(id);
  const canWrite = hasPermission(principal, PERMISSION.documentsWrite);

  if (doc.isPending) {
    return (
      <Page eyebrow={t("{code} · library", { code: site.code })} title="…">
        <Skeleton rows={6} height={18} />
      </Page>
    );
  }
  if (doc.isError) {
    return (
      <Page
        eyebrow={t("{code} · library", { code: site.code })}
        title={id}
        actions={<Link to="/library">{t("← Library")}</Link>}
      >
        <ErrorState error={doc.error} retry={() => void doc.refetch()} />
      </Page>
    );
  }
  const d = doc.data;
  const type = types.data?.find((t) => t.key === d.type);
  const att = attention(d, now);
  const current = d.revisionHistory.find((r) => r.status === "issued");
  const acksCurrent = d.acknowledgements.filter((a) => a.isCurrent);
  const outstanding = acksCurrent.filter((a) => a.required && !a.acknowledgedAt);
  const mine = pendingAcknowledgement(d, principal.name);
  const mineRevision = mine ? revisionByLabel(d, mine.revision) : undefined;

  return (
    <Page
      eyebrow={t("{code} · library · {type}", { code: site.code, type: type?.name ?? d.type })}
      title={
        <span style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {d.title}
          <StatusPill status={d.status} />
          <ClassPill c={d.classification} />
        </span>
      }
      actions={
        <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
          {d.status !== "withdrawn" && (
            <Button
              variant="primary"
              onClick={() => setDrafting(true)}
              disabled={!canWrite}
              title={
                canWrite
                  ? t("Draft the next revision")
                  : t("Requires {permission}", { permission: PERMISSION.documentsWrite })
              }
            >
              {t("New revision")}
            </Button>
          )}
          <Link to="/library">{t("← Library")}</Link>
        </span>
      }
    >
      {drafting && (
        <NewRevisionDialog
          doc={d}
          type={type}
          onClose={() => setDrafting(false)}
          onCreated={() => setDrafting(false)}
        />
      )}
      <div className={s.detail}>
        <div style={{ display: "grid", gap: "var(--gap)" }}>
          {d.classification === "restricted" && (
            <div className={s.restricted}>
              {t("Restricted. Opening this document is logged with your name and address.")}
            </div>
          )}
          {d.status === "withdrawn" && (
            <div className={s.withdrawn}>
              {d.withdrawnReason
                ? t("Withdrawn {date} — {reason}. Kept for the record; not to be used.", {
                    date: d.withdrawnAt ? formatDate(d.withdrawnAt) : "",
                    reason: d.withdrawnReason,
                  })
                : t("Withdrawn {date}. Kept for the record; not to be used.", {
                    date: d.withdrawnAt ? formatDate(d.withdrawnAt) : "",
                  })}
            </div>
          )}
          <Card>
            <div className={s.facts}>
              <Fact label={t("Reference")} value={d.reference} mono />
              <Fact label={t("Applies at")} value={d.siteId ? site.code : t("All sites")} />
              <Fact label={t("Current revision")} value={d.revision} mono />
              <Fact label={t("Issued")} value={d.issuedOn ? formatDate(d.issuedOn) : undefined} />
              <Fact
                label={t("Review due")}
                value={d.reviewDueOn ? formatDate(d.reviewDueOn) : undefined}
                warn={att === "review_overdue" || att === "review_due"}
              />
              <Fact
                label={t("Expires")}
                value={
                  d.expiresOn ? formatDate(d.expiresOn) : type?.expires === false ? t("does not expire") : undefined
                }
                warn={att === "expired" || att === "expiring"}
              />
              <Fact
                label={t("Controlled")}
                value={
                  type
                    ? type.controlled
                      ? type.requiresApproval
                        ? t("yes, approval required")
                        : t("yes")
                      : t("no")
                    : undefined
                }
              />
              <Fact
                label={t("Retention")}
                value={
                  d.retentionUntil
                    ? t("until {date}", { date: formatDate(d.retentionUntil) })
                    : type?.retentionYears
                      ? t("{n} years", { n: type.retentionYears })
                      : undefined
                }
              />
            </div>
            {att !== "ok" && (
              <div
                className={s.attention}
                style={{
                  marginTop: 12,
                  color: att === "expired" || att === "review_overdue" ? "var(--red)" : "var(--amber)",
                }}
              >
                {t(ATTENTION_LABEL[att])}
              </div>
            )}
            {d.tags.length > 0 && (
              <div className={s.tags} style={{ marginTop: 10 }}>
                {d.tags.map((tag) => (
                  <span key={tag} className={s.tag}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card title="Revisions" sub="Every revision, newest first">
            <div className={s.revs} data-testid="revisions">
              {d.revisionHistory.map((r) => (
                <RevisionRow key={r.id} r={r} current={r.id === current?.id} documentId={d.id} type={type} />
              ))}
              {d.revisionHistory.length === 0 && <div className="muted">{t("No revisions recorded.")}</div>}
            </div>
          </Card>
        </div>

        <div style={{ display: "grid", gap: "var(--gap)" }}>
          <Card
            title="Acknowledgements"
            sub={
              acksCurrent.length
                ? t("{n} of {total} for the current issue", {
                    n: acksCurrent.length - outstanding.length,
                    total: acksCurrent.length,
                  })
                : undefined
            }
          >
            {mine && mineRevision && (
              <div style={{ marginBottom: 10, display: "grid", gap: 6 }}>
                <div style={{ fontSize: 13.5 }}>
                  {t("You are required to read")} <b>{mine.revision}</b>.
                </div>
                <Button
                  variant="primary"
                  onClick={() => acknowledge.mutate(mineRevision.id)}
                  disabled={acknowledge.isPending}
                >
                  {acknowledge.isPending ? t("Recording…") : t("I have read this revision")}
                </Button>
                {acknowledge.isError && (
                  <div className={s.refusal} role="alert">
                    {t("Refused: {reason}", { reason: (acknowledge.error as Error).message })}
                  </div>
                )}
                {acknowledge.data && !acknowledge.data.acknowledged && (
                  <div className="muted" style={{ fontSize: 13 }}>
                    {t("The platform found no outstanding acknowledgement for you on this revision.")}
                  </div>
                )}
              </div>
            )}
            {acksCurrent.length === 0 ? (
              <div className="muted" style={{ fontSize: 13.5 }}>
                {t("No acknowledgement required for this document.")}
              </div>
            ) : (
              <div className={s.acks}>
                {acksCurrent.map((a, i) => (
                  <div key={i} className={`${s.ack} ${a.required && !a.acknowledgedAt ? s.ackMissing : ""}`}>
                    <span>{a.person}</span>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {a.acknowledgedAt ? formatDate(a.acknowledgedAt) : a.required ? t("outstanding") : t("optional")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card title="Linked to" sub={d.links.length ? undefined : "nothing yet"}>
            {d.links.length > 0 && (
              <div className={s.acks}>
                {d.links.map((l, i) => (
                  <div key={i} className={s.ack}>
                    <span>
                      <span className="mono muted">{l.entityKind}</span> {linkTo(l.entityKind, l.entityId)}
                    </span>
                    <span className="mono muted" style={{ fontSize: 12 }}>
                      {l.relation ?? ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </Page>
  );
}

function linkTo(kind: string, id: string) {
  if (kind === "work_order") return <Link to={`/work-orders/${id}`}>{id.slice(0, 8)}…</Link>;
  if (kind === "alert") return <Link to={`/alerts?open=${id}`}>{id}</Link>;
  if (kind === "map_layer") return <Link to="/airfield">{id}</Link>;
  if (kind === "rule") return <Link to="/compliance?view=rules">{id}</Link>;
  return <span className="mono">{id}</span>;
}

function RevisionRow({
  r,
  current,
  documentId,
  type,
}: {
  r: Revision;
  current: boolean;
  documentId: string;
  type: DocumentType | undefined;
}) {
  const { principal } = useSession();
  const transition = useTransitionRevision(documentId);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const offered = offeredTransitions(r.status, type);

  const go = (to: RevisionStatus, withNote?: string) => {
    transition.mutate(
      { revisionId: r.id, to, note: withNote },
      {
        onSuccess: () => {
          setRejecting(false);
          setNote("");
        },
      },
    );
  };

  return (
    <div className={`${s.rev} ${current ? s.revCurrent : ""}`} data-testid={`revision-${r.id}`}>
      <div>
        <div className={s.revId}>{r.revision}</div>
        <div className={s.revMeta}>{t(REVISION_STATUS_LABEL[r.status])}</div>
      </div>
      <div>
        <div>
          {r.issuedOn
            ? t("Issued {date}", { date: formatDate(r.issuedOn) })
            : t("Created {date}", { date: formatDateTime(r.createdAt) })}
          {r.author && <span className="muted"> · {r.author}</span>}
          {r.approvedBy && <span className="muted"> · {t("approved by {name}", { name: r.approvedBy })}</span>}
        </div>
        {r.changeNote && <div className={s.revNote}>{r.changeNote}</div>}
        {r.rejectedReason && <div className={s.revNote}>{t("Rejected: {reason}", { reason: r.rejectedReason })}</div>}
        {r.supersededAt && (
          <div className={s.revMeta}>{t("superseded {date}", { date: formatDate(r.supersededAt) })}</div>
        )}
        {transition.isError && (
          <div className={s.refusal} role="alert" style={{ marginTop: 6 }}>
            {t("Refused: {reason}", { reason: (transition.error as Error).message })}
          </div>
        )}
        {rejecting && (
          <div className={s.rejectNote}>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("Why it is rejected — recorded on the revision")}
              aria-label={t("Rejection reason for {revision}", { revision: r.revision })}
              autoFocus
            />
            <Button
              variant="primary"
              disabled={!note.trim() || transition.isPending}
              onClick={() => go("rejected", note.trim())}
            >
              {t("Reject")}
            </Button>
            <Button onClick={() => setRejecting(false)}>{t("Cancel")}</Button>
          </div>
        )}
      </div>
      <div className={s.revMeta} style={{ textAlign: "end" }}>
        {r.bytes != null && `${(r.bytes / 1024).toFixed(0)} KB`}
        {r.sha256 && <div title={r.sha256}>sha256 {r.sha256.slice(0, 8)}</div>}
        {offered.length > 0 && (
          <div className={s.revActions}>
            {offered.map((to) => {
              const key = transitionPermission(to);
              const allowed = hasPermission(principal, key);
              return (
                <Button
                  key={to}
                  size="sm"
                  variant={to === "approved" || to === "issued" ? "primary" : "default"}
                  disabled={!allowed || transition.isPending || rejecting}
                  title={
                    allowed ? `→ ${t(REVISION_STATUS_LABEL[to])}` : t("Requires {permission}", { permission: key })
                  }
                  aria-label={`${t(TRANSITION_VERB[to])} ${r.revision}`}
                  onClick={() => (to === "rejected" ? setRejecting(true) : go(to))}
                >
                  {t(TRANSITION_VERB[to])}
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value, mono, warn }: { label: string; value?: string; mono?: boolean; warn?: boolean }) {
  return (
    <div className={s.fact}>
      <span className={s.factLabel}>{label}</span>
      <span
        className={`${s.factValue} ${mono ? "mono" : ""}`}
        style={warn ? { color: "var(--amber)", fontWeight: 600 } : undefined}
      >
        {value ?? <span className="muted">—</span>}
      </span>
    </div>
  );
}
